import React, { useState, useEffect } from 'react';
import { Card, Table, Statistic, Row, Col, Button, Space, Input, Tag, Typography, Tooltip, message, Checkbox, DatePicker, Select, Modal, Form, Drawer, Alert, Popover, Divider } from 'antd';
import { DownloadOutlined, FilterOutlined, ReloadOutlined, ExportOutlined, BellOutlined, PlayCircleOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, StopOutlined, EyeOutlined, QuestionCircleOutlined, ClearOutlined } from '@ant-design/icons';
import { UserTableAnalytics as UserTableAnalyticsData, UserTableMetrics, UserTableFilters, Campaign } from '../../../types/analytics';
import { ANALYTICS_API_URL } from '../../../constants/config';
import { getAndSanitize } from '../../../shared/utils/sanitization';
import { campaignService } from '../../../services/campaignService';
import dayjs from 'dayjs';

  // Estilos CSS para filas seleccionadas
const tableStyles = `
  .selected-row {
    background-color: #e6f7ff !important;
    border-left: 3px solid #1890ff;
  }
  .selected-row:hover {
    background-color: #bae7ff !important;
  }
  .ant-table-tbody > tr.selected-row > td {
    background-color: #e6f7ff !important;
  }
  .ant-table-tbody > tr.selected-row:hover > td {
    background-color: #bae7ff !important;
  }

  /* Mantener fondo sólido en columnas fijas */
  .ant-table-cell-fix-left,
  .ant-table-cell-fix-left-last {
    background-color: #1f1f1f !important;
    position: sticky !important;
    z-index: 2 !important;
  }

  .ant-table-thead > tr > th.ant-table-cell-fix-left,
  .ant-table-thead > tr > th.ant-table-cell-fix-left-last {
    background-color: #141414 !important;
    z-index: 3 !important;
  }

  .ant-table-tbody > tr > td.ant-table-cell-fix-left,
  .ant-table-tbody > tr > td.ant-table-cell-fix-left-last {
    background-color: #1f1f1f !important;
  }

  .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left,
  .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left-last {
    background-color: #262626 !important;
  }

  .ant-table-tbody > tr.selected-row > td.ant-table-cell-fix-left,
  .ant-table-tbody > tr.selected-row > td.ant-table-cell-fix-left-last {
    background-color: #e6f7ff !important;
  }

  .ant-table-tbody > tr.selected-row:hover > td.ant-table-cell-fix-left,
  .ant-table-tbody > tr.selected-row:hover > td.ant-table-cell-fix-left-last {
    background-color: #bae7ff !important;
  }

  /* Sombra para indicar que la columna está fija */
  .ant-table-cell-fix-left-last::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 30px;
    transform: translateX(100%);
    transition: box-shadow 0.3s;
    content: '';
    pointer-events: none;
  }

  .ant-table-ping-left .ant-table-cell-fix-left-last::after {
    box-shadow: inset 10px 0 8px -8px rgba(0, 0, 0, 0.45);
  }

  .filter-section {
    margin-bottom: 24px;
    padding: 20px;
    background: #141414;
    border-radius: 12px;
    border: 1px solid #303030;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
    position: relative;
  }

  .filter-section:hover {
    box-shadow: 0 4px 16px rgba(24, 144, 255, 0.2);
    border-color: #1890ff;
    transform: translateY(-1px);
  }

  .filter-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    gap: 12px;
  }

  .filter-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
  }

  .filter-title {
    font-size: 16px;
    font-weight: 600;
    color: #1890ff;
    margin: 0;
  }

  .filter-description {
    font-size: 13px;
    color: #8c8c8c;
    margin: 4px 0 0 0;
    line-height: 1.5;
  }

  .info-popover .ant-popover-content {
    max-width: 300px;
  }

  .filter-input-group {
    background: #1f1f1f;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #303030;
    margin-top: 12px;
  }
  
  .filter-actions {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: #141414;
    padding: 20px 24px;
    border-top: 1px solid #303030;
    z-index: 10;
  }

  .active-filter-indicator {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 10px;
    height: 10px;
    background: #52c41a;
    border-radius: 50%;
    border: 2px solid #141414;
    box-shadow: 0 0 8px rgba(82, 196, 26, 0.6);
  }
`;
  const { Text, Title } = Typography;
  const { Option } = Select;
  const { TextArea } = Input;

  interface UserTableAnalyticsProps {
    loading: boolean;
    adminId?: string;
    dateRange?: {
      start: string;
      end: string;
    };
  }

  // Interface para el filtro de regreso después de inactividad está ahora en types/analytics.ts

  interface UserTableAnalyticsState {
    data: UserTableAnalyticsData | null;
    loading: boolean;
    filters: Partial<UserTableFilters>;
    showAdvancedFilters: boolean;
    currentPage: number;
    pageSize: number;
    campaigns: Campaign[];
    campaignsLoading: boolean;
    showCampaigns: boolean;
    selectedCampaign: Campaign | null;
    selectedUsers: string[];
    campaignModal: {
      visible: boolean;
      campaign?: Campaign;
      mode: 'create' | 'edit' | 'view';
    };
    broadcastingDateRange: {
      start: string;
      end: string;
    };
  }
// Filter explanation content for popovers
const filterExplanations = {
  dateRange: {
    title: "Rango de Tiempo Independiente",
    content: (
      <div>
        <p><strong>Propósito:</strong> Define el período específico para el análisis de broadcasting, independiente de los filtros globales.</p>
        <p><strong>Uso:</strong> Permite analizar comportamientos en períodos específicos sin afectar otros reportes.</p>
        <p><strong>Ejemplo:</strong> Analizar actividad del último mes para campaña de reactivación.</p>
      </div>
    )
  },
  basicFilters: {
    title: "Filtros Básicos de Broadcasting",
    content: (
      <div>
        <p><strong>Con/Sin cargas:</strong> Segmenta usuarios según su historial de depósitos.</p>
        <p><strong>Rango de cargas:</strong> Filtra por cantidad específica de transacciones.</p>
        <p><strong>Casos de uso:</strong></p>
        <ul>
          <li>Solo con cargas: Usuarios con experiencia previa</li>
          <li>Solo sin cargas: Nuevos usuarios o inactivos totales</li>
          <li>Rango específico: Segmentación por nivel de actividad</li>
        </ul>
      </div>
    )
  },
  campaignStatus: {
    title: "Estado de Interacción con Campañas",
    content: (
      <div>
        <p><strong>Nunca Contactado:</strong> Usuarios sin historial de campañas - máximo potencial.</p>
        <p><strong>Contactado Sin Respuesta:</strong> Usuarios que recibieron campañas pero no respondieron.</p>
        <p><strong>Contactado y Respondió:</strong> Usuarios que interactuaron con campañas anteriores.</p>
        <p><strong>Contactado y Convirtió:</strong> Usuarios que completaron acciones tras campañas.</p>
        <p><em>Nota: Los filtros son mutuamente excluyentes para evitar solapamientos.</em></p>
      </div>
    )
  },
  lastLoad: {
    title: "Filtros de Última Actividad",
    content: (
      <div>
        <p><strong>En período actual:</strong> Usuarios activos en el rango seleccionado.</p>
        <p><strong>Día específico:</strong> Precisión temporal para campañas dirigidas.</p>
        <p><strong>Estrategia:</strong> Ideal para reactivación inmediata de usuarios recientes.</p>
      </div>
    )
  },
  withdrawals: {
    title: "Análisis de Comportamiento de Retiros",
    content: (
      <div>
        <p><strong>Propósito:</strong> Entender patrones de retiro para segmentación avanzada.</p>
        <p><strong>Métricas clave:</strong></p>
        <ul>
          <li>Frecuencia: Indica confianza en la plataforma</li>
          <li>Volumen: Muestra nivel de actividad financiera</li>
        </ul>
        <p><strong>Aplicación:</strong> Identificar usuarios conservadores vs. arriesgados.</p>
      </div>
    )
  },
  hourlyBehavior: {
    title: "Patrones de Comportamiento Horario",
    content: (
      <div>
        <p><strong>Diurno (6:00-20:00):</strong> Usuarios activos en horario laboral/vespertino.</p>
        <p><strong>Nocturno (20:00-6:00):</strong> Usuarios con actividad en horarios nocturnos.</p>
        <p><strong>Mixto:</strong> Usuarios sin patrón horario definido.</p>
        <p><strong>Aplicación:</strong> Optimizar timing de envío de campañas según hábitos del usuario.</p>
      </div>
    )
  },
  highValue: {
    title: "Segmentación por Alto Valor",
    content: (
      <div>
        <p><strong>Definición:</strong> Usuarios cuya suma total de depósitos supera el umbral definido.</p>
        <p><strong>Estrategia VIP:</strong> Atención personalizada y campañas exclusivas.</p>
        <p><strong>Retención:</strong> Prioritarios para evitar migración a competencia.</p>
        <p><strong>Recomendación:</strong> Configurar según percentil 80-90 de tu base de usuarios.</p>
      </div>
    )
  },
  inactivity: {
    title: "Detección de Inactividad Prolongada",
    content: (
      <div>
        <p><strong>Objetivo:</strong> Identificar usuarios en riesgo de abandono.</p>
        <p><strong>Configuración flexible:</strong> Desde minutos hasta meses según el contexto.</p>
        <p><strong>Casos de uso:</strong></p>
        <ul>
          <li>Minutos/Horas: Usuarios en sesión que se detuvieron</li>
          <li>Días/Semanas: Reactivación de usuarios dormidos</li>
          <li>Meses: Recuperación de usuarios perdidos</li>
        </ul>
      </div>
    )
  },
  mostActiveDay: {
    title: "Día de Mayor Actividad",
    content: (
      <div>
        <p><strong>Análisis predictivo:</strong> Identifica el día de la semana con mayor actividad de depósitos.</p>
        <p><strong>Optimización de campañas:</strong> Envía mensajes cuando el usuario está más receptivo.</p>
        <p><strong>Segmentación temporal:</strong> Agrupa usuarios por patrones semanales similares.</p>
        <p><strong>Ejemplo:</strong> Usuarios más activos los viernes pueden responder mejor a campañas de fin de semana.</p>
      </div>
    )
  },
  returnAfterInactivity: {
    title: "Detección de Regreso Post-Inactividad",
    content: (
      <div>
        <p><strong>Concepto avanzado:</strong> Detecta usuarios que regresaron después de un período de ausencia.</p>
        <p><strong>Configuración dual:</strong></p>
        <ul>
          <li><strong>Período reciente:</strong> Donde SÍ hubo actividad (regreso)</li>
          <li><strong>Período anterior:</strong> Donde NO hubo actividad (ausencia)</li>
        </ul>
        <p><strong>Valor estratégico:</strong> Usuarios que ya demostraron capacidad de retorno - alta probabilidad de reenganche.</p>
        <p><strong>Ejemplo:</strong> Usuario inactivo toda la semana que regresó el fin de semana.</p>
      </div>
    )
  },
  singleCycle: {
    title: "Análisis de Ciclo Único Completo",
    content: (
      <div>
        <p><strong>Definición:</strong> Usuarios que completaron exactamente UN ciclo "Depósito → Retiro".</p>
        <p><strong>Perfil objetivo:</strong> Conocen el proceso completo pero no se engancharon.</p>
        <p><strong>Oportunidad dorada:</strong></p>
        <ul>
          <li>Superaron la barrera inicial de confianza</li>
          <li>Experimentaron el flujo completo</li>
          <li>Candidatos ideales para remarketing dirigido</li>
        </ul>
        <p><strong>Estrategia:</strong> Enfoque en beneficios adicionales y mejoras de experiencia.</p>
      </div>
    )
  },
  amountAnalysis: {
    title: "Análisis de Desviación de Montos",
    content: (
      <div>
        <p><strong>Detección de anomalías:</strong> Compara el último monto con el promedio histórico del usuario.</p>
        <p><strong>Configuración:</strong></p>
        <ul>
          <li><strong>Tipo:</strong> Depósitos o retiros</li>
          <li><strong>Umbral:</strong> Porcentaje de diferencia significativa</li>
          <li><strong>Historial mínimo:</strong> Operaciones necesarias para cálculo confiable</li>
        </ul>
        <p><strong>Casos detectados:</strong></p>
        <ul>
          <li><strong>Mayor:</strong> Posible incremento de confianza o capacidad</li>
          <li><strong>Menor:</strong> Posible reducción de confianza o cambio financiero</li>
        </ul>
      </div>
    )
  },
  highValueInactive: {
    title: "VIP Inactivos - Riesgo de Pérdida",
    content: (
      <div>
        <p><strong>Alerta crítica:</strong> Usuarios de alto valor que están inactivos.</p>
        <p><strong>Doble filtro:</strong></p>
        <ul>
          <li>Superan umbral de valor total depositado</li>
          <li>Sin depósitos en período específico</li>
        </ul>
        <p><strong>Prioridad máxima:</strong></p>
        <ul>
          <li>Riesgo de migración a competencia</li>
          <li>Impacto significativo en revenue</li>
          <li>Requieren atención personalizada inmediata</li>
        </ul>
        <p><strong>Acción recomendada:</strong> Contacto directo del equipo VIP.</p>
      </div>
    )
  }
};

// Helper component for filter sections
const FilterSection: React.FC<{
  icon: string;
  title: string;
  description: string;
  explanationKey: keyof typeof filterExplanations;
  children: React.ReactNode;
  hasActiveFilters?: boolean;
}> = ({ icon, title, description, explanationKey, children, hasActiveFilters = false }) => {
  const explanation = filterExplanations[explanationKey];
  
  return (
    <div className="filter-section">
      {hasActiveFilters && <div className="active-filter-indicator" />}
      <div className="filter-header">
        <div className="filter-icon">{icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h4 className="filter-title">{title}</h4>
            <Popover
              content={explanation.content}
              title={explanation.title}
              trigger="hover"
              placement="right"
              overlayClassName="info-popover"
            >
              <Button 
                type="text" 
                size="small" 
                icon={<QuestionCircleOutlined />}
                style={{ 
                  color: '#1890ff', 
                  fontSize: '14px',
                  padding: '4px',
                  minWidth: 'auto',
                  height: 'auto'
                }}
              />
            </Popover>
          </div>
          <p className="filter-description">{description}</p>
        </div>
      </div>
      <div className="filter-input-group">
        {children}
      </div>
    </div>
  );
};
  export const UserTableAnalytics: React.FC<UserTableAnalyticsProps> = ({ 
    loading: parentLoading, 
    adminId,
    dateRange
  }) => {
    const [state, setState] = useState<UserTableAnalyticsState>({
      data: null,
      loading: false,
      filters: {
        showUsersWithLoads: false,
        showUsersWithoutLoads: false
      },
      showAdvancedFilters: false,
      currentPage: 1,
      pageSize: 15,
      campaigns: [],
      campaignsLoading: false,
      showCampaigns: false,
      selectedCampaign: null,
      selectedUsers: [],
      campaignModal: {
        visible: false,
        mode: 'create',
      },
      broadcastingDateRange: {
        start: dayjs().subtract(30, 'days').format('DD/MM/YYYY'),
        end: dayjs().format('DD/MM/YYYY')
      },
    });

    // State for drawer visibility
    const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);

    // Function to save campaign-specific filter configuration
  const saveCampaignFilterConfig = async (campaignId: string, filters: Partial<UserTableFilters>, customDateRange?: { start: string; end: string }) => {
    try {
      const token = getAndSanitize('token');
      if (!token || !adminId) {
        console.error('[Broadcasting] No token or adminId available for saving filter config');
        return;
      }

      const dateRangeToSave = customDateRange || state.broadcastingDateRange;
      
      // Validate dates before sending
      const hasValidDates = dateRangeToSave.start && dateRangeToSave.end && 
                           dateRangeToSave.start !== '' && dateRangeToSave.end !== '';
      

      const requestBody: any = {
        campaignId,
        filters,
        createdBy: adminId
      };

      // Only add dates if they are valid
      if (hasValidDates) {
        requestBody.startDate = dateRangeToSave.start + 'T00:00:00.000Z';
        requestBody.endDate = dateRangeToSave.end + 'T23:59:59.999Z';
      } else {
        requestBody.startDate = null;
        requestBody.endDate = null;
      }

      const response = await fetch(`${ANALYTICS_API_URL}/api/broadcasting/campaign-filters`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {

        message.success(`Configuración guardada para campaña: ${state.selectedCampaign?.name || 'Seleccionada'}`);
      } else {
        const errorData = await response.json();
        console.error('[Broadcasting] ❌ Server error saving config:', errorData);
        throw new Error(`Failed to save filter configuration: ${errorData.message || response.statusText}`);
      }
    } catch (error) {
      console.error('[Broadcasting] ❌ Error saving campaign filter config:', error);
      message.error('Error al guardar la configuración de filtros');
    }
  };

    // Function to load campaign-specific filter configuration
    const loadCampaignFilterConfig = async (campaignId: string) => {
      try {
        const token = getAndSanitize('token');
        if (!token) {
          console.error('[Broadcasting] No token available for loading filter config');
          return null;
        }

        const response = await fetch(`${ANALYTICS_API_URL}/api/broadcasting/campaign-filters/${campaignId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            
            const loadedDateRange = {
              start: result.data.startDate ? dayjs(result.data.startDate).format('DD/MM/YYYY') : state.broadcastingDateRange.start,
              end: result.data.endDate ? dayjs(result.data.endDate).format('DD/MM/YYYY') : state.broadcastingDateRange.end
            };
            
            
            setState(prev => ({
              ...prev,
              filters: result.data.filters || {},
              broadcastingDateRange: loadedDateRange
            }));
            
            message.success(`Configuración cargada para: ${state.selectedCampaign?.name || 'Campaña seleccionada'}`);
            return {
              ...result.data,
              filters: result.data.filters || {},
              dateRange: loadedDateRange
            };
          }
        }
        return null;
      } catch (error) {
        console.error('[Broadcasting] ❌ Error loading campaign filter config:', error);
        message.error('Error al cargar la configuración de filtros');
        return null;
      }
    };

    // Function to handle broadcasting date range changes (solo actualiza el estado, no aplica filtros)
    const handleBroadcastingDateRangeChange = (_dates: any, dateStrings: [string, string]) => {
      const newDateRange = {
        start: dateStrings[0],
        end: dateStrings[1]
      };


      setState(prev => ({
        ...prev,
        broadcastingDateRange: newDateRange
      }));
    };

  const fetchData = async (filters: Partial<UserTableFilters> = {}, page: number = state.currentPage, size: number = state.pageSize) => {
    if (!adminId) {
      return;
    }

    setState(prev => ({ ...prev, loading: true }));

    try {
      const token = getAndSanitize('token');
      if (!token) {
        message.error('Token no disponible');
        return;
      }

      const offset = (page - 1) * size;

      const queryParams = new URLSearchParams({
        adminId,
        startDate: state.broadcastingDateRange.start,
        endDate: state.broadcastingDateRange.end,
        limit: size.toString(),
        offset: offset.toString(),
        ...(state.selectedCampaign && {
          campaignId: state.selectedCampaign.id
        }),
        ...Object.entries(filters).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (key === 'inactivityPeriod' && typeof value === 'object' && 'value' in value && 'unit' in value) {
              acc['inactivityPeriod.value'] = value.value.toString();
              acc['inactivityPeriod.unit'] = value.unit;
            } 
            else if (key === 'returnAfterInactivity' && typeof value === 'object' && 'action' in value) {
              acc['returnAfterInactivity.action'] = value.action;
              acc['returnAfterInactivity.recentPeriod.start'] = value.recentPeriod.start;
              acc['returnAfterInactivity.recentPeriod.end'] = value.recentPeriod.end;
              acc['returnAfterInactivity.inactivePeriod.start'] = value.inactivePeriod.start;
              acc['returnAfterInactivity.inactivePeriod.end'] = value.inactivePeriod.end;
            }
            else if (key === 'lastAmountVsAverage' && typeof value === 'object' && 'operationType' in value) {
              acc['lastAmountVsAverage.operationType'] = value.operationType;
              acc['lastAmountVsAverage.comparisonType'] = value.comparisonType;
              acc['lastAmountVsAverage.significanceThreshold'] = value.significanceThreshold.toString();
              acc['lastAmountVsAverage.minimumOperations'] = value.minimumOperations.toString();
            }
            // NUEVO FILTRO: Alto valor pero inactivo
            else if (key === 'highValueButInactiveFilter' && typeof value === 'object' && 'minTotalDeposits' in value) {
              acc['highValueButInactiveFilter.minTotalDeposits'] = value.minTotalDeposits.toString();
              acc['highValueButInactiveFilter.daysSinceLastDeposit'] = value.daysSinceLastDeposit.toString();
            }
            else if (key === 'highValueThreshold') {
              acc[key] = value.toString();
            }
            else if (key === 'mostActiveDay') {
              acc[key] = value.toString();
            }
            else if (key === 'singleCompleteCycleOnly') {
              acc[key] = value.toString();
            }
            else {
              acc[key] = value.toString();
            }
          }
          return acc;
        }, {} as Record<string, string>)
      });


      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      const response = await fetch(`${ANALYTICS_API_URL}/api/broadcasting/analytics?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setState(prev => ({ 
          ...prev, 
          data: result.data, 
          loading: false,
          currentPage: page,
          pageSize: size
        }));
        
        message.success(`Cargados ${result.data.users.length} usuarios de ${result.data.totalUsersFiltered} filtrados`, 2);
      } else {
        throw new Error(result.message || 'Error al obtener datos');
      }
    } catch (error: any) {
      console.error('[Broadcasting UserTableAnalytics] Error:', error);
      
      if (error.name === 'AbortError') {
        message.error('La consulta tardó demasiado tiempo. Intenta reducir el rango de fechas o ajustar los filtros.');
      } else {
        message.error('Error al cargar los datos de usuarios');
      }
      
      setState(prev => ({ ...prev, loading: false }));
    }
  };
    // PAGINACIÓN REAL - Función para manejar cambios de página
    const handlePageChange = async (page: number, pageSize?: number) => {
      const newPageSize = pageSize || state.pageSize;
      
      // Limpiar selección al cambiar página
      setState(prev => ({ ...prev, selectedUsers: [] }));
      
      // Fetch data with new pagination
      await fetchData(state.filters, page, newPageSize);
    };

    // PAGINACIÓN REAL - Función para manejar cambio de tamaño de página
    const handlePageSizeChange = async (_: number, size: number) => {
      
      // Reset to first page when changing page size
      setState(prev => ({ ...prev, selectedUsers: [] }));
      await fetchData(state.filters, 1, size);
    };

    // Inicializar datos al montar componente
    useEffect(() => {
      if (adminId) {
        fetchData(state.filters);
      }
    }, [adminId]);

    const handleFilterChange = async (key: string, value: any) => {
      const newFilters = {
        ...state.filters,
        [key]: value
      };
      
      setState(prev => ({
        ...prev,
        filters: newFilters
      }));

      if (state.selectedCampaign) {
        setTimeout(async () => {
          await saveCampaignFilterConfig(state.selectedCampaign!.id, newFilters);
        }, 500);
      }
    };

    const applyFilters = async () => {

      // PAGINACIÓN REAL - Reset a primera página al aplicar filtros
      setState(prev => ({ ...prev, selectedUsers: [] }));
      await fetchData(state.filters, 1, state.pageSize);

      // Guardar configuración de campaña incluyendo el rango de fechas
      if (state.selectedCampaign) {
        await saveCampaignFilterConfig(state.selectedCampaign.id, state.filters, state.broadcastingDateRange);
      }
    };

  const clearFilters = async () => {
    const cleanFilters = {
      showUsersWithLoads: false,
      showUsersWithoutLoads: false,
      minLoads: undefined,
      maxLoads: undefined,
      lastLoadInPeriod: undefined,
      specificLastLoadDate: undefined,
      minWithdrawals: undefined,
      maxWithdrawals: undefined,
      depositBehavior: undefined,
      withdrawalBehavior: undefined,
      neverContacted: undefined,
      contactedNoResponse: undefined,
      contactedAndResponded: undefined,
      contactedAndConverted: undefined,
      highValueThreshold: undefined,
      inactivityPeriod: undefined,
      mostActiveDay: undefined,
      returnAfterInactivity: undefined,
      lastAmountVsAverage: undefined,
      singleCompleteCycleOnly: undefined,
      // NUEVO: Limpiar filtro alto valor pero inactivo
      highValueButInactiveFilter: undefined
    };
    setState(prev => ({
      ...prev,
      filters: cleanFilters,
      selectedUsers: []
    }));
    await fetchData(cleanFilters, 1, state.pageSize);
  };

    // Función para manejar selección de campaña
    const handleSelectCampaign = async (campaign: Campaign) => {
      
      if (state.selectedCampaign && state.selectedCampaign.id !== campaign.id) {
        await saveCampaignFilterConfig(state.selectedCampaign.id, state.filters, state.broadcastingDateRange);
      }
      
      setState(prev => ({ 
        ...prev, 
        selectedCampaign: campaign,
        selectedUsers: []
      }));
      
      const savedConfig = await loadCampaignFilterConfig(campaign.id);
      if (savedConfig) {
        await fetchData(savedConfig.filters, 1, state.pageSize);
      } else {
        await fetchData(state.filters, 1, state.pageSize);
      }
    };

    const handleDeselectCampaign = async () => {
      if (state.selectedCampaign) {
        await saveCampaignFilterConfig(state.selectedCampaign.id, state.filters, state.broadcastingDateRange);
      }
      
      setState(prev => ({ ...prev, selectedCampaign: null }));
    };

    // Funciones de selección de usuarios
    const handleUserSelection = (selectedRowKeys: React.Key[], _: any[]) => {
      const userIds = selectedRowKeys.map(key => String(key));
      setState(prev => ({
        ...prev,
        selectedUsers: userIds
      }));
    };

    const clearUserSelection = () => {
      setState(prev => ({
        ...prev,
        selectedUsers: []
      }));
      message.info('Selección de usuarios limpiada');
    };

    // Función para marcar usuario como convertido
    const handleMarkUserAsConverted = async (userId: string, username: string) => {
      try {
        const token = getAndSanitize('token');
        if (!token) {
          message.error('Token no disponible');
          return;
        }

        const response = await fetch(`${ANALYTICS_API_URL}/api/campaign-conversion/mark-converted`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId
          })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            message.success(`Usuario ${username} marcado como convertido exitosamente`);
            await fetchData(state.filters, state.currentPage, state.pageSize);
          } else {
            message.error(result.message || 'Error al marcar usuario como convertido');
          }
        } else {
          const errorData = await response.json();
          message.error(errorData.message || 'Error al marcar usuario como convertido');
        }
      } catch (error) {
        console.error('[UserTableAnalytics] Error marking user as converted:', error);
        message.error('Error al marcar usuario como convertido');
      }
    };

    // Función para marcar múltiples usuarios como convertidos
    const handleBulkMarkUsersAsConverted = async () => {
      if (state.selectedUsers.length === 0) {
        message.warning('No hay usuarios seleccionados');
        return;
      }

      try {
        const token = getAndSanitize('token');
        if (!token) {
          message.error('Token no disponible');
          return;
        }

        const response = await fetch(`${ANALYTICS_API_URL}/api/campaign-conversion/bulk-mark-converted`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userIds: state.selectedUsers
          })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            message.success(`${result.data.successCount} usuarios marcados como convertidos exitosamente`);
            if (result.data.errorCount > 0) {
              message.warning(`${result.data.errorCount} usuarios no pudieron ser procesados`);
            }
            await fetchData(state.filters, state.currentPage, state.pageSize);
            setState(prev => ({ ...prev, selectedUsers: [] }));
          } else {
            message.error(result.message || 'Error al marcar usuarios como convertidos');
          }
        } else {
          const errorData = await response.json();
          message.error(errorData.message || 'Error al marcar usuarios como convertidos');
        }
      } catch (error) {
        console.error('[UserTableAnalytics] Error bulk marking users as converted:', error);
        message.error('Error al marcar usuarios como convertidos');
      }
    };

    // Cargar campañas
    const fetchCampaigns = async () => {
      try {
        setState(prev => ({ ...prev, campaignsLoading: true }));
        const result = await campaignService.getCampaigns({ limit: 50 });
        setState(prev => ({ 
          ...prev, 
          campaigns: result.campaigns,
          campaignsLoading: false 
        }));
      } catch (error: any) {
        console.error('Error fetching campaigns:', error);
        setState(prev => ({ ...prev, campaignsLoading: false }));
      }
    };

    // Cargar campañas al montar el componente
    useEffect(() => {
      fetchCampaigns();
    }, []);

    // Funciones de gestión de campañas
    const handleCreateCampaign = () => {
      if (!state.data || state.data.users.length === 0) {
        message.warning('No hay usuarios para crear la campaña. Ajusta los filtros.');
        return;
      }

      const selectedUserIds = state.selectedUsers.length > 0 ? state.selectedUsers : [];

      setState(prev => ({
        ...prev,
        campaignModal: {
          visible: true,
          mode: 'create',
        }
      }));

      if (selectedUserIds.length > 0) {
        message.info(`Campaña se creará con ${selectedUserIds.length} usuarios seleccionados`);
      } else {
        message.info('Campaña se creará con todos los usuarios filtrados');
      }
    };

    const handleViewCampaign = (campaign: Campaign) => {
      setState(prev => ({
        ...prev,
        campaignModal: {
          visible: true,
          campaign,
          mode: 'view',
        }
      }));
    };

    const handleEditCampaign = (campaign: Campaign) => {
      setState(prev => ({
        ...prev,
        campaignModal: {
          visible: true,
          campaign,
          mode: 'edit',
        }
      }));
    };

    const handleCloseCampaignModal = () => {
      setState(prev => ({
        ...prev,
        campaignModal: {
          visible: false,
          mode: 'create',
        }
      }));
    };

    const handleSaveCampaign = async (campaignData: { name: string; objective: 'REMARKETING' | 'POSTBLOQUEO'; message: string }) => {
      try {
        if (state.campaignModal.mode === 'create') {
          const campaignFilters: UserTableFilters = {
            dateRange: dateRange || {
              start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
              end: new Date().toISOString()
            },
            adminId: adminId || '',
            token: getAndSanitize('token') || '',
            ...state.filters
          };

          const selectedUserIds = state.selectedUsers.length > 0 ? state.selectedUsers : [];
          
          const newCampaign = await campaignService.createCampaign({
            ...campaignData,
            filtersConfig: campaignFilters,
            selectedUserIds: selectedUserIds
          });

          message.success(`Campaña "${newCampaign.name}" creada para ${selectedUserIds.length > 0 ? selectedUserIds.length : state.data?.totalUsersFiltered || 0} usuarios`);
          
          setState(prev => ({ ...prev, selectedUsers: [] }));
        } else if (state.campaignModal.mode === 'edit' && state.campaignModal.campaign) {
          await campaignService.updateCampaign(state.campaignModal.campaign.id, campaignData);
          message.success('Campaña actualizada exitosamente');
        }

        fetchCampaigns();
        handleCloseCampaignModal();
        setState(prev => ({ ...prev, showCampaigns: true }));
      } catch (error: any) {
        message.error(error.message || 'Error al guardar campaña');
      }
    };

    const handleCampaignStatusChange = async (campaignId: string, status: Campaign['status']) => {
      try {
        await campaignService.updateCampaignStatus(campaignId, status);
        message.success('Estado de campaña actualizado');
        fetchCampaigns();
      } catch (error: any) {
        message.error(error.message || 'Error al actualizar estado');
      }
    };

    const handleDeleteCampaign = async (campaignId: string) => {
      try {
        await campaignService.deleteCampaign(campaignId);
        message.success('Campaña eliminada');
        fetchCampaigns();
      } catch (error: any) {
        message.error(error.message || 'Error al eliminar campaña');
      }
    };

  const exportData = async (format: 'csv' | 'excel' = 'excel') => {
    if (!state.data) return;

    // Confirmación antes de exportar
    const totalToExport = state.data.totalUsersFiltered;
    const confirmed = window.confirm(
      `¿Deseas exportar ${totalToExport} usuario${totalToExport !== 1 ? 's' : ''} filtrado${totalToExport !== 1 ? 's' : ''} en formato ${format.toUpperCase()}?`
    );

    if (!confirmed) return;

    try {
      const token = getAndSanitize('token');
      if (!token) {
        message.error('Token no disponible');
        return;
      }

      // Convertir fechas DD/MM/YYYY a formato ISO para el backend
      const startDateISO = dayjs(state.broadcastingDateRange.start, 'DD/MM/YYYY').toISOString();
      const endDateISO = dayjs(state.broadcastingDateRange.end, 'DD/MM/YYYY').toISOString();

      const queryParams = new URLSearchParams({
        adminId: adminId || '',
        format,
        startDate: startDateISO,
        endDate: endDateISO,
        ...(state.selectedCampaign && {
          campaignId: state.selectedCampaign.id
        }),
        ...Object.entries(state.filters).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (key === 'inactivityPeriod' && typeof value === 'object' && 'value' in value && 'unit' in value) {
              acc['inactivityPeriod.value'] = value.value.toString();
              acc['inactivityPeriod.unit'] = value.unit;
            }
            else if (key === 'returnAfterInactivity' && typeof value === 'object' && 'action' in value) {
              acc['returnAfterInactivity.action'] = value.action;
              acc['returnAfterInactivity.recentPeriod.start'] = value.recentPeriod.start;
              acc['returnAfterInactivity.recentPeriod.end'] = value.recentPeriod.end;
              acc['returnAfterInactivity.inactivePeriod.start'] = value.inactivePeriod.start;
              acc['returnAfterInactivity.inactivePeriod.end'] = value.inactivePeriod.end;
            }
            else if (key === 'lastAmountVsAverage' && typeof value === 'object' && 'operationType' in value) {
              acc['lastAmountVsAverage.operationType'] = value.operationType;
              acc['lastAmountVsAverage.comparisonType'] = value.comparisonType;
              acc['lastAmountVsAverage.significanceThreshold'] = value.significanceThreshold.toString();
              acc['lastAmountVsAverage.minimumOperations'] = value.minimumOperations.toString();
            }
            // NUEVO FILTRO: Alto valor pero inactivo
            else if (key === 'highValueButInactiveFilter' && typeof value === 'object' && 'minTotalDeposits' in value) {
              acc['highValueButInactiveFilter.minTotalDeposits'] = value.minTotalDeposits.toString();
              acc['highValueButInactiveFilter.daysSinceLastDeposit'] = value.daysSinceLastDeposit.toString();
            }
            else if (key === 'highValueThreshold') {
              acc[key] = value.toString();
            }
            else if (key === 'mostActiveDay') {
              acc[key] = value.toString();
            }
            else if (key === 'singleCompleteCycleOnly') {
              acc[key] = value.toString();
            }
            else {
              acc[key] = value.toString();
            }
          }
          return acc;
        }, {} as Record<string, string>)
      });


      const response = await fetch(`${ANALYTICS_API_URL}/api/broadcasting/export?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `broadcasting_user_table_${dayjs().format('YYYY-MM-DD_HH-mm')}.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success(`Datos de broadcasting exportados exitosamente como ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting data:', error);
      message.error('Error al exportar los datos de broadcasting');
    }
  };



    const formatCurrency = (value: number | null) => {
      if (value === null || value === undefined) return '-';
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS'
      }).format(value);
    };

    const formatDate = (date: any) => {
      if (!date || date === null || date === undefined) return '-';
      
      if (typeof date === 'object' && Object.keys(date).length === 0) return '-';
      
      if (typeof date === 'string' && date.trim() === '') return '-';
      
      try {
        const parsedDate = dayjs(date);
        if (!parsedDate.isValid()) return '-';
        return parsedDate.format('DD/MM/YYYY HH:mm');
      } catch {
        return '-';
      }
    };

    const getDaysSinceLastLoad = (lastLoadDate: string | null) => {
      if (!lastLoadDate) return null;
      const daysDiff = dayjs().diff(dayjs(lastLoadDate), 'days');
      return daysDiff;
    };

    const getLoadFrequencyColor = (days: number | null) => {
      if (days === null) return 'default';
      if (days <= 7) return 'green';
      if (days <= 30) return 'orange';
      return 'red';
    };

    const formatTimeWithoutActivity = (days: number | null, activityType: string) => {
      if (days === null) return `Sin ${activityType}`;
      if (days === 0) return 'Hoy';
      if (days === 1) return '1 día';
      if (days <= 7) return `${days} días`;
      if (days <= 30) return `${Math.floor(days / 7)} semanas`;
      if (days <= 365) return `${Math.floor(days / 30)} meses`;
      return `${Math.floor(days / 365)} años`;
    };

    const getActivityColor = (days: number | null) => {
      if (days === null) return 'default';
      if (days <= 1) return 'green';
      if (days <= 7) return 'blue';
      if (days <= 30) return 'orange';
      return 'red';
    };

    const formatBehavior = (behavior: 'diurno' | 'nocturno' | 'mixto' | null) => {
      if (!behavior) return '-';
      const colors = {
        'diurno': 'gold',
        'nocturno': 'purple',
        'mixto': 'cyan'
      };
      const icons = {
        'diurno': '☀️',
        'nocturno': '🌙',
        'mixto': '🌗'
      };
      return <Tag color={colors[behavior]}>{icons[behavior]} {behavior.charAt(0).toUpperCase() + behavior.slice(1)}</Tag>;
    };

    // Definir columnas de la tabla
  const columns = [
    {
      title: 'Usuario',
      key: 'userInfo',
      width: 200,
      fixed: 'left' as const,
      render: (record: any) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Text strong style={{ fontSize: '13px' }}>{record.username}</Text>
            {record.isHighValue && (
              <Tooltip title="Usuario de alto valor">
                <Tag color="gold" style={{ fontSize: '10px', padding: '0 4px' }}>
                  💎 VIP
                </Tag>
              </Tooltip>
            )}
            {/* NUEVO: Indicador de regreso después de inactividad */}
            {record.returnedAfterInactivity && (
              <Tooltip title="Usuario que regresó después de un período de inactividad">
                <Tag color="green" style={{ fontSize: '10px', padding: '0 4px' }}>
                  🔄 Retorno
                </Tag>
              </Tooltip>
            )}
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {record.phone && <div>📱 {record.phone}</div>}
            {record.accountName && <div>🏦 {record.accountName}</div>}
          </div>
        </div>
      )
    },

      {
        title: 'Admin',
        dataIndex: 'adminInfo',
        key: 'adminInfo',
        width: 100,
        render: (adminInfo: any) => (
          <Tooltip title={adminInfo.email || 'Sin email'}>
            <Tag color="purple">{adminInfo.username}</Tag>
          </Tooltip>
        )
      },
      {
        title: 'Actividad Depósitos',
        key: 'depositActivity',
        width: 160,
        render: (record: any) => (
          <div>
            <div>
              <Tag color={record.totalLoads > 0 ? 'green' : 'default'}>
                {record.totalLoads} cargas
              </Tag>
            </div>
            <div style={{ fontSize: '10px', marginTop: '2px', display: 'flex', justifyContent: 'space-between' }}>
              <span>Mín: {formatCurrency(record.minDeposit)}</span>
              <span>Máx: {formatCurrency(record.maxDeposit)}</span>
            </div>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#1890ff' }}>
              Total: {formatCurrency(record.totalDepositAmount)}
            </div>
          </div>
        )
      },
      {
        title: 'Última Actividad',
        key: 'lastActivity',
        width: 140,
        render: (record: any) => {
          const days = getDaysSinceLastLoad(record.lastLoadDate);
          return (
            <div>
              <div style={{ fontSize: '11px' }}>{formatDate(record.lastLoadDate)}</div>
              {days !== null && (
                <Tag color={getLoadFrequencyColor(days)}>
                  {days === 0 ? 'Hoy' : `${days} días`}
                </Tag>
              )}
            </div>
          );
        }
      },
      {
        title: 'Tiempo sin Actividad',
        key: 'timeWithoutActivity',
        width: 180,
        render: (record: any) => (
          <div>
            <div style={{ marginBottom: '2px' }}>
              <Tag color={getActivityColor(record.daysSinceLastDeposit)}>
                Sin depósito: {formatTimeWithoutActivity(record.daysSinceLastDeposit, 'depósitos')}
              </Tag>
            </div>
            <div>
              <Tag color={getActivityColor(record.daysSinceLastWithdrawal)}>
                Sin retiro: {formatTimeWithoutActivity(record.daysSinceLastWithdrawal, 'retiros')}
              </Tag>
            </div>
          </div>
        )
      },
      {
        title: 'Actividad Retiros',
        key: 'withdrawalActivity',
        width: 150,
        render: (record: any) => (
          <div>
            <div>
              <Tag color={record.totalWithdrawals > 0 ? 'orange' : 'default'}>
                {record.totalWithdrawals} retiros
              </Tag>
            </div>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#fa8c16', marginTop: '2px' }}>
              Total: {formatCurrency(record.totalWithdrawalAmount)}
            </div>
          </div>
        )
      },
        {
      title: 'Día Más Activo',
      key: 'mostActiveDay',
      width: 120,
      render: (record: any) => {
        if (!record.mostActiveWeekday) return <Text type="secondary">-</Text>;
        
        const dayTranslations: { [key: string]: string } = {
          monday: 'Lunes',
          tuesday: 'Martes',
          wednesday: 'Miércoles',
          thursday: 'Jueves',
          friday: 'Viernes',
          saturday: 'Sábado',
          sunday: 'Domingo'
        };
        
        const dayEmojis: { [key: string]: string } = {
          monday: '🌙',
          tuesday: '🔥',
          wednesday: '🌟',
          thursday: '⚡',
          friday: '🎉',
          saturday: '🌈',
          sunday: '☀️'
        };
        
        return (
          <Tag color="blue">
            {dayEmojis[record.mostActiveWeekday]} {dayTranslations[record.mostActiveWeekday]}
          </Tag>
        );
      }
    },
    
    // Actualizar columna de tiempo sin actividad para usar el nuevo campo
    {
      title: 'Inactividad',
      key: 'inactivity',
      width: 120,
      render: (record: any) => {
        const days = record.daysSinceLastActivity;
        if (days === null) return <Tag color="default">Sin actividad</Tag>;
        
        let color = 'green';
        let text = '';
        
        if (days === 0) {
          text = 'Hoy';
          color = 'green';
        } else if (days === 1) {
          text = '1 día';
          color = 'green';
        } else if (days <= 7) {
          text = `${days} días`;
          color = 'blue';
        } else if (days <= 30) {
          text = `${Math.floor(days / 7)} semanas`;
          color = 'orange';
        } else if (days <= 365) {
          text = `${Math.floor(days / 30)} meses`;
          color = 'red';
        } else {
          text = `${Math.floor(days / 365)} años`;
          color = 'red';
        }
        
        return <Tag color={color}>{text}</Tag>;
      }
    },
  {
    title: 'Patrones Horarios',
    key: 'behaviorPatterns',
    width: 140,
    render: (record: any) => (
      <div>
        <div style={{ marginBottom: '3px' }}>
          <Text style={{ fontSize: '10px', color: '#666' }}>Depósitos:</Text>
          <div>{formatBehavior(record.depositBehavior)}</div>
        </div>
        <div>
          <Text style={{ fontSize: '10px', color: '#666' }}>Retiros:</Text>
          <div>{formatBehavior(record.withdrawalBehavior)}</div>
        </div>
      </div>
    )
  },
  // Agregar nueva columna a la tabla (después de las columnas existentes):
  {
    title: 'Análisis de Montos',
    key: 'amountAnalysis',
    width: 180,
    render: (record: any) => {
      if (!record.hasSignificantAmountDifference || !record.lastAmountVsAverageInfo) {
        return <Text type="secondary">-</Text>;
      }
      
      const info = record.lastAmountVsAverageInfo;
      const isGreater = info.percentageDifference > 0;
      
      return (
        <div>
          <div style={{ marginBottom: '4px' }}>
            <Tag color={isGreater ? 'red' : 'green'} style={{ fontSize: '10px' }}>
              {isGreater ? '📈' : '📉'} {Math.abs(info.percentageDifference).toFixed(1)}%
            </Tag>
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            <div>Último: {formatCurrency(info.lastAmount)}</div>
            <div>Promedio: {formatCurrency(info.historicalAverage)}</div>
          </div>
          <div style={{ fontSize: '10px', color: '#999' }}>
            {info.totalHistoricalOps} ops históricas
          </div>
        </div>
      );
    }
  },
      {
        title: 'Campañas',
        key: 'campaigns',
        width: 120,
        render: (record: any) => (
          <div>
            <div>
              <Tag color={record.totalCampaigns > 0 ? 'purple' : 'default'}>
                {record.totalCampaigns} campaña{record.totalCampaigns !== 1 ? 's' : ''}
              </Tag>
            </div>
            {record.activeCampaigns > 0 && (
              <div style={{ marginTop: '2px' }}>
                <Tag color="processing">
                  {record.activeCampaigns} activa{record.activeCampaigns !== 1 ? 's' : ''}
                </Tag>
              </div>
            )}
            {record.successfulCampaigns > 0 && (
              <div style={{ marginTop: '2px' }}>
                <Tag color="success">
                  {record.successfulCampaigns} exitosa{record.successfulCampaigns !== 1 ? 's' : ''}
                </Tag>
              </div>
            )}
          </div>
        )
      },
      {
        title: 'Respuesta Post-Campaña',
        key: 'campaignResponse',
        width: 140,
        align: 'center' as const,
        render: (record: any) => {
          if (record.totalCampaigns === 0) {
            return <Text type="secondary">Sin campañas</Text>;
          }
          
          if (record.hasRecentCampaignSuccess) {
            return (
              <div>
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  Respondió recientemente
                </Tag>
                <div style={{ fontSize: '10px', color: '#52c41a', marginTop: '2px' }}>
                  ¡Campaña exitosa!
                </div>
              </div>
            );
          }
          
          if (record.successfulCampaigns > 0) {
            return (
              <Tag color="cyan">
                Respondió anteriormente
              </Tag>
            );
          }
          
          if (record.activeCampaigns > 0) {
            return (
              <Tag color="warning">
                En campaña activa
              </Tag>
            );
          }
          
          return (
            <Tag color="default">
              Sin respuesta
            </Tag>
          );
        }
      },
      {
        title: 'Estado Conversión',
        key: 'conversionStatus',
        width: 130,
        align: 'center' as const,
        render: (record: any) => {
          if (record.wasConverted) {
            return (
              <div>
                <Tag color="success" icon={<CheckCircleOutlined />}>
                  Convertido
                </Tag>
                {record.convertedAt && (
                  <div style={{ fontSize: '10px', color: '#52c41a', marginTop: '2px' }}>
                    {formatDate(record.convertedAt)}
                  </div>
                )}
              </div>
            );
          }
          
          if (record.successfulCampaigns > 0) {
            return (
              <div>
                <Tag color="orange">
                  Pendiente
                </Tag>
                <div style={{ marginTop: '4px' }}>
                  <Tooltip title="Marcar como convertido">
                    <Button
                      size="small"
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkUserAsConverted(record.userId, record.username);
                      }}
                      style={{ fontSize: '10px', height: '20px', padding: '0 6px' }}
                    >
                      Convertir
                    </Button>
                  </Tooltip>
                </div>
              </div>
            );
          }
          
          if (record.totalCampaigns > 0) {
            return (
              <Tag color="default">
                Sin conversión
              </Tag>
            );
          }
          
          return (
            <Tag color="default">
              N/A
            </Tag>
          );
        }
      },
      {
        title: 'Registro & Estadía',
        key: 'registrationInfo',
        width: 150,
        render: (record: any) => (
          <div>
            <div style={{ fontSize: '11px' }}>{formatDate(record.createdAt)}</div>
            <Tag color="blue" style={{ fontSize: '10px', marginTop: '2px' }}>
              {record.systemAge || 'Calculando...'}
            </Tag>
            {record.averageLoadInterval && (
              <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                Intervalo: {Math.round(record.averageLoadInterval)} días
              </div>
            )}
          </div>
        )
      }
    ];

    const isLoading = parentLoading || state.loading;

return (
  <div>
    <style>{tableStyles}</style>
    
    {/* Sección de Campañas */}
    <Card 
      title={
        <Space>
          <BellOutlined style={{ color: '#1890ff' }} />
          <Text strong>Campañas de Broadcasting</Text>
          <Tag color="blue">{state.campaigns.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Button
            icon={<BellOutlined />}
            onClick={handleCreateCampaign}
            disabled={!state.data || state.data.totalUsersFiltered === 0}
            style={{ backgroundColor: '#722ed1', borderColor: '#722ed1', color: 'white' }}
          >
            Crear Campaña
            {state.selectedUsers.length > 0 && (
              <Tag color="green" style={{ marginLeft: 8 }}>
                {state.selectedUsers.length} seleccionados
              </Tag>
            )}
          </Button>
          {state.selectedUsers.length > 0 && (
            <Button
              icon={<CheckCircleOutlined />}
              onClick={handleBulkMarkUsersAsConverted}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
              loading={state.loading}
            >
              Marcar Convertidos ({state.selectedUsers.length})
            </Button>
          )}
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchCampaigns}
            loading={state.campaignsLoading}
            size="small"
          >
            Actualizar
          </Button>
        </Space>
      }
      style={{ marginBottom: '24px' }}
    >
      {state.campaigns.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <BellOutlined style={{ fontSize: '32px', color: '#d9d9d9', marginBottom: '12px' }} />
          <Text type="secondary" style={{ display: 'block', marginBottom: '12px' }}>
            No hay campañas creadas. Aplica filtros en la tabla y crea tu primera campaña.
          </Text>
        </div>
      ) : (
        <>
          {/* Estados de campañas */}
          <Row gutter={16} style={{ marginBottom: '16px' }}>
            {state.campaigns.filter(c => c.status === 'DRAFT').length > 0 && (
              <Col xs={24} md={12}>
                <Card 
                  size="small"
                  style={{ 
                    background: 'linear-gradient(135deg, #f6ffed 0%, #f0f9ff 100%)',
                    border: '1px solid #52c41a'
                  }}
                >
                  <Space>
                    <span style={{ fontSize: '20px' }}>🚀</span>
                    <div>
                      <Text strong style={{ color: '#389e0d' }}>
                        {state.campaigns.filter(c => c.status === 'DRAFT').length} Lista{state.campaigns.filter(c => c.status === 'DRAFT').length !== 1 ? 's' : ''} para Iniciar
                      </Text>
                      <div><Text type="secondary" style={{ fontSize: '12px' }}>Haz clic en "Iniciar" para activar</Text></div>
                    </div>
                  </Space>
                </Card>
              </Col>
            )}
            
            {state.campaigns.filter(c => c.status === 'IN_PROGRESS').length > 0 && (
              <Col xs={24} md={12}>
                <Card 
                  size="small"
                  style={{ 
                    background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f9ff 100%)',
                    border: '1px solid #1890ff'
                  }}
                >
                  <Space>
                    <span style={{ fontSize: '20px' }}>⚡</span>
                    <div>
                      <Text strong style={{ color: '#1890ff' }}>
                        {state.campaigns.filter(c => c.status === 'IN_PROGRESS').length} en Progreso
                      </Text>
                      <div><Text type="secondary" style={{ fontSize: '12px' }}>Campañas ejecutándose</Text></div>
                    </div>
                  </Space>
                </Card>
              </Col>
            )}
          </Row>

          {/* Tabla de campañas compacta */}
          <Table
            dataSource={state.campaigns}
            loading={state.campaignsLoading}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 3, showSizeChanger: false, size: 'small' }}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: state.selectedCampaign ? [state.selectedCampaign.id] : [],
              onSelect: (record: Campaign) => handleSelectCampaign(record),
              onSelectNone: () => handleDeselectCampaign(),
            }}
            onRow={(record: Campaign) => ({
              onClick: () => handleSelectCampaign(record),
              className: state.selectedCampaign?.id === record.id ? 'selected-campaign-row' : '',
              style: {
                cursor: 'pointer',
                backgroundColor: state.selectedCampaign?.id === record.id ? '#e6f7ff' : undefined,
                border: state.selectedCampaign?.id === record.id ? '2px solid #1890ff' : undefined,
              }
            })}
            columns={[
              {
                title: 'Campaña',
                dataIndex: 'name',
                key: 'name',
                render: (text: string, record: Campaign) => (
                  <div>
                    <Text strong style={{ fontSize: '13px' }}>{text}</Text>
                    <div>
                      <Tag 
                        color={record.objective === 'REMARKETING' ? 'blue' : 'orange'}
                        style={{ fontSize: '10px' }}
                      >
                        {record.objective === 'REMARKETING' ? 'Remarketing' : 'Post-bloqueo'}
                      </Tag>
                    </div>
                  </div>
                ),
              },
              {
                title: 'Estado',
                dataIndex: 'status',
                key: 'status',
                render: (status: Campaign['status']) => {
                  const statusConfig = {
                    DRAFT: { color: 'warning', text: 'Lista', icon: <PlayCircleOutlined /> },
                    IN_PROGRESS: { color: 'processing', text: 'Activa', icon: <PlayCircleOutlined /> },
                    COMPLETED: { color: 'success', text: 'Completada', icon: <CheckCircleOutlined /> },
                    CANCELLED: { color: 'error', text: 'Cancelada', icon: <StopOutlined /> },
                  };
                  const config = statusConfig[status];
                  return (
                    <Tag color={config.color} icon={config.icon}>
                      {config.text}
                    </Tag>
                  );
                },
              },
              {
                title: 'Usuarios',
                key: 'users',
                align: 'center' as const,
                render: (record: Campaign) => (
                  <Text strong style={{ color: '#1890ff' }}>{record.selectedUsers || 0}</Text>
                ),
              },
              {
                title: 'Fecha',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (date: string) => (
                  <Text style={{ fontSize: '11px' }}>{dayjs(date).format('DD/MM HH:mm')}</Text>
                ),
              },
              {
                title: 'Acciones',
                key: 'actions',
                render: (record: Campaign) => (
                  <Space size="small">
                    <Tooltip title="Ver">
                      <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewCampaign(record)} />
                    </Tooltip>
                    {record.status === 'DRAFT' && (
                      <>
                        <Tooltip title="Editar">
                          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditCampaign(record)} />
                        </Tooltip>
                        <Tooltip title="Iniciar">
                          <Button
                            size="small"
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            onClick={() => handleCampaignStatusChange(record.id, 'IN_PROGRESS')}
                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                          />
                        </Tooltip>
                      </>
                    )}
                    {record.status === 'IN_PROGRESS' && (
                      <Tooltip title="Completar">
                        <Button size="small" icon={<CheckCircleOutlined />} onClick={() => handleCampaignStatusChange(record.id, 'COMPLETED')} />
                      </Tooltip>
                    )}
                    {record.status !== 'IN_PROGRESS' && (
                      <Tooltip title="Eliminar">
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteCampaign(record.id)} />
                      </Tooltip>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        </>
      )}
    </Card>

    {/* Resumen de estadísticas - Compacto */}
    {state.data && (
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 8]}>
          <Col xs={12} sm={8} md={4}>
            <Statistic
              title="Total Filtrados"
              value={state.data.totalUsersFiltered}
              valueStyle={{ color: '#52c41a', fontSize: '18px' }}
              style={{ textAlign: 'center' }}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic
              title="Con Cargas"
              value={state.data.summary.totalUsersWithLoads}
              suffix={state.data.totalUsersFiltered > 0 ? 
                `(${((state.data.summary.totalUsersWithLoads / state.data.totalUsersFiltered) * 100).toFixed(0)}%)` : ''}
              valueStyle={{ color: '#fa541c', fontSize: '18px' }}
              style={{ textAlign: 'center' }}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic
              title="Promedio Cargas"
              value={state.data.summary.averageLoadsPerUser}
              precision={1}
              valueStyle={{ color: '#722ed1', fontSize: '18px' }}
              style={{ textAlign: 'center' }}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic
              title="Total Depositado"
              value={state.data.summary.totalDepositAmount}
              formatter={(value) => formatCurrency(Number(value))}
              valueStyle={{ color: '#fa8c16', fontSize: '18px' }}
              style={{ textAlign: 'center' }}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic
              title="En Período"
              value={state.data.totalUsers}
              valueStyle={{ color: '#1890ff', fontSize: '18px' }}
              style={{ textAlign: 'center' }}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Statistic
              title="Página"
              value={state.data.pagination.currentPage}
              suffix={`/${state.data.pagination.totalPages}`}
              valueStyle={{ color: '#13c2c2', fontSize: '18px' }}
              style={{ textAlign: 'center' }}
            />
          </Col>
        </Row>
      </Card>
    )}

    <Card>
      {/* Indicador de campaña seleccionada */}
      {state.selectedCampaign && (
        <div style={{ 
          marginBottom: 16, 
          padding: 12, 
          backgroundColor: '#e6f7ff', 
          border: '1px solid #1890ff', 
          borderRadius: 6 
        }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <BellOutlined style={{ color: '#1890ff' }} />
                <Text strong style={{ color: '#1890ff' }}>
                  Campaña Seleccionada: {state.selectedCampaign.name}
                </Text>
                <Tag color={state.selectedCampaign.objective === 'REMARKETING' ? 'blue' : 'orange'}>
                  {state.selectedCampaign.objective === 'REMARKETING' ? 'Remarketing' : 'Post-bloqueo'}
                </Tag>
                <Tag color="green">
                  Los filtros se guardan automáticamente
                </Tag>
              </Space>
            </Col>
            <Col>
              <Button 
                size="small" 
                onClick={handleDeselectCampaign}
                icon={<DeleteOutlined />}
              >
                Deseleccionar
              </Button>
            </Col>
          </Row>
        </div>
      )}

      {/* Indicador de usuarios seleccionados */}
      {state.selectedUsers.length > 0 && (
        <div style={{ 
          marginBottom: 16, 
          padding: 12, 
          backgroundColor: '#f6ffed', 
          border: '1px solid #52c41a', 
          borderRadius: 6 
        }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <span style={{ fontSize: '16px' }}>👥</span>
                <Text strong style={{ color: '#389e0d' }}>
                  {state.selectedUsers.length} Usuario{state.selectedUsers.length !== 1 ? 's' : ''} Seleccionado{state.selectedUsers.length !== 1 ? 's' : ''}
                </Text>
                <Tag color="green">
                  Listo para campaña
                </Tag>
              </Space>
            </Col>
            <Col>
              <Space>
                <Button 
                  size="small" 
                  onClick={clearUserSelection}
                  icon={<DeleteOutlined />}
                >
                  Limpiar Selección
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
      )}

      {/* Filtros y controles */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Button
            type="primary"
            icon={<FilterOutlined />}
            onClick={() => setFilterDrawerVisible(true)}
            size="large"
          >
            Filtros Avanzados
            {Object.values(state.filters).some(filter => 
              filter !== undefined && filter !== false && filter !== null && filter !== ''
            ) && (
              <Tag color="orange" style={{ marginLeft: 8 }}>
                Activos
              </Tag>
            )}
          </Button>
          
          {state.selectedUsers.length > 0 && (
            <Button
              icon={<DeleteOutlined />}
              onClick={clearUserSelection}
              size="large"
              danger
            >
              Limpiar Selección ({state.selectedUsers.length})
            </Button>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchData(state.filters, state.currentPage, state.pageSize)}
            loading={isLoading}
            size="large"
          >
            Actualizar
          </Button>
        </Space>
        <Space>
          <Button
            icon={<ExportOutlined />}
            onClick={() => exportData('csv')}
            disabled={!state.data}
            size="large"
          >
            Exportar CSV
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => exportData('excel')}
            disabled={!state.data}
            size="large"
          >
            Exportar Excel
          </Button>
        </Space>
      </div>

      {/* Enhanced Filter Drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              color: 'white'
            }}>
              🎯
            </div>
            <div>
              <h3 style={{ margin: 0, color: '#1890ff' }}>Filtros Avanzados de Broadcasting</h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#8c8c8c' }}>
                Configura criterios específicos para segmentación de usuarios
              </p>
            </div>
          </div>
        }
        placement="right"
        width={500}
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
        bodyStyle={{ padding: 0, background: '#0a0a0a' }}
        style={{ zIndex: 1000 }}
        headerStyle={{ background: '#141414', borderBottom: '1px solid #303030' }}
      >
        <div style={{ height: 'calc(100% - 84px)', overflowY: 'auto', padding: '24px', background: '#0a0a0a' }}>
          
          {/* Rango de Tiempo Independiente */}
          <FilterSection
            icon="📅"
            title="Rango de Tiempo"
            description="Define el período específico para análisis de broadcasting"
            explanationKey="dateRange"
            hasActiveFilters={!!(state.broadcastingDateRange.start && state.broadcastingDateRange.end)}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Seleccionar período:</Text>
                <DatePicker.RangePicker
                  value={[
                    state.broadcastingDateRange.start ? dayjs(state.broadcastingDateRange.start, 'DD/MM/YYYY') : null,
                    state.broadcastingDateRange.end ? dayjs(state.broadcastingDateRange.end, 'DD/MM/YYYY') : null
                  ]}
                  onChange={handleBroadcastingDateRangeChange}
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  placeholder={['Fecha inicio', 'Fecha fin']}
                />
                {state.selectedCampaign && (
                  <div style={{ marginTop: 8 }}>
                    <Tag color="blue" icon={<BellOutlined />} style={{ fontSize: '12px' }}>
                      Configuración guardada para: {state.selectedCampaign.name}
                    </Tag>
                  </div>
                )}
              </Col>
            </Row>
          </FilterSection>

          {/* Filtros Básicos */}
          <FilterSection
            icon="⚡"
            title="Filtros Básicos"
            description="Segmentación fundamental por actividad de depósitos"
            explanationKey="basicFilters"
            hasActiveFilters={!!(state.filters.showUsersWithLoads || state.filters.showUsersWithoutLoads || state.filters.minLoads || state.filters.maxLoads)}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Checkbox
                    checked={state.filters.showUsersWithLoads === true}
                    onChange={(e) => handleFilterChange('showUsersWithLoads', e.target.checked || undefined)}
                  >
                    <Text strong>Solo usuarios con cargas</Text>
                  </Checkbox>
                  <Checkbox
                    checked={state.filters.showUsersWithoutLoads === true}
                    onChange={(e) => handleFilterChange('showUsersWithoutLoads', e.target.checked || undefined)}
                  >
                    <Text strong>Solo usuarios sin cargas</Text>
                  </Checkbox>
                </Space>
              </Col>
              <Col span={12}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Mínimo cargas:</Text>
                <Input
                  type="number"
                  placeholder="Ej: 1"
                  value={state.filters.minLoads}
                  onChange={(e) => handleFilterChange('minLoads', e.target.value ? parseInt(e.target.value) : undefined)}
                  min={0}
                />
              </Col>
              <Col span={12}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Máximo cargas:</Text>
                <Input
                  type="number"
                  placeholder="Ej: 100"
                  value={state.filters.maxLoads}
                  onChange={(e) => handleFilterChange('maxLoads', e.target.value ? parseInt(e.target.value) : undefined)}
                  min={0}
                />
              </Col>
            </Row>
          </FilterSection>

          {/* Estado de Campaña */}
          <FilterSection
            icon="📊"
            title="Estado de Campañas"
            description="Segmentación por historial de interacción con campañas"
            explanationKey="campaignStatus"
            hasActiveFilters={!!(state.filters.neverContacted || state.filters.contactedNoResponse || state.filters.contactedAndResponded || state.filters.contactedAndConverted)}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Checkbox
                    checked={state.filters.neverContacted === true}
                    onChange={(e) => {
                      const newValue = e.target.checked ? true : undefined;
                      let newFilters = { ...state.filters, neverContacted: newValue };
                      if (newValue) {
                        delete newFilters.contactedNoResponse;
                        delete newFilters.contactedAndResponded;
                        delete newFilters.contactedAndConverted;
                      }
                      setState(prev => ({ ...prev, filters: newFilters }));
                      handleFilterChange('neverContacted', newValue);
                    }}
                  >
                    <div>
                      <Text strong>Nunca Contactado</Text>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Sin historial de campañas - máximo potencial</div>
                    </div>
                  </Checkbox>
                  <Checkbox
                    checked={state.filters.contactedNoResponse === true}
                    onChange={(e) => {
                      const newValue = e.target.checked ? true : undefined;
                      let newFilters = { ...state.filters, contactedNoResponse: newValue };
                      if (newValue) {
                        delete newFilters.neverContacted;
                        delete newFilters.contactedAndResponded;
                        delete newFilters.contactedAndConverted;
                      }
                      setState(prev => ({ ...prev, filters: newFilters }));
                      handleFilterChange('contactedNoResponse', newValue);
                    }}
                  >
                    <div>
                      <Text strong>Contactado Sin Respuesta</Text>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Recibió campañas pero no respondió</div>
                    </div>
                  </Checkbox>
                  <Checkbox
                    checked={state.filters.contactedAndResponded === true}
                    onChange={(e) => {
                      const newValue = e.target.checked ? true : undefined;
                      let newFilters = { ...state.filters, contactedAndResponded: newValue };
                      if (newValue) {
                        delete newFilters.neverContacted;
                        delete newFilters.contactedNoResponse;
                        delete newFilters.contactedAndConverted;
                      }
                      setState(prev => ({ ...prev, filters: newFilters }));
                      handleFilterChange('contactedAndResponded', newValue);
                    }}
                  >
                    <div>
                      <Text strong>Contactado y Respondió</Text>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Interactuó con campañas anteriores</div>
                    </div>
                  </Checkbox>
                  <Checkbox
                    checked={state.filters.contactedAndConverted === true}
                    onChange={(e) => {
                      const newValue = e.target.checked ? true : undefined;
                      let newFilters = { ...state.filters, contactedAndConverted: newValue };
                      if (newValue) {
                        delete newFilters.neverContacted;
                        delete newFilters.contactedNoResponse;
                        delete newFilters.contactedAndResponded;
                      }
                      setState(prev => ({ ...prev, filters: newFilters }));
                      handleFilterChange('contactedAndConverted', newValue);
                    }}
                  >
                    <div>
                      <Text strong>Contactado y Convirtió</Text>
                      <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Completó acciones tras campañas</div>
                    </div>
                  </Checkbox>
                </Space>
              </Col>
            </Row>
          </FilterSection>

          {/* Última Actividad */}
          <FilterSection
            icon="🕒"
            title="Última Actividad"
            description="Precisión temporal para campañas dirigidas"
            explanationKey="lastLoad"
            hasActiveFilters={!!(state.filters.lastLoadInPeriod || state.filters.specificLastLoadDate)}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Checkbox
                  checked={state.filters.lastLoadInPeriod}
                  onChange={(e) => handleFilterChange('lastLoadInPeriod', e.target.checked ? true : undefined)}
                >
                  <div>
                    <Text strong>Solo última carga en período actual</Text>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Usuarios activos en el rango seleccionado</div>
                  </div>
                </Checkbox>
                {state.filters.lastLoadInPeriod && (
                  <div style={{ marginTop: 8, marginLeft: 24 }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Período: {dateRange ? `${dayjs(dateRange.start).format('DD/MM/YYYY')} - ${dayjs(dateRange.end).format('DD/MM/YYYY')}` : 'No definido'}
                    </Text>
                  </div>
                )}
              </Col>
              <Col span={24}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Día específico de última carga:</Text>
                <DatePicker
                  value={state.filters.specificLastLoadDate ? dayjs(state.filters.specificLastLoadDate) : null}
                  onChange={(date) => handleFilterChange('specificLastLoadDate', date ? date.format('YYYY-MM-DD') : undefined)}
                  placeholder="Seleccionar día específico"
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  disabled={!dateRange}
                  disabledDate={(current) => {
                    if (!dateRange) return true;
                    const start = dayjs(dateRange.start).startOf('day');
                    const end = dayjs(dateRange.end).endOf('day');
                    return current && (current.isBefore(start) || current.isAfter(end));
                  }}
                />
              </Col>
            </Row>
          </FilterSection>

          {/* Análisis de Retiros */}
          <FilterSection
            icon="💸"
            title="Análisis de Retiros"
            description="Comportamiento de retiros para segmentación avanzada"
            explanationKey="withdrawals"
            hasActiveFilters={!!(state.filters.minWithdrawals || state.filters.maxWithdrawals)}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Mínimo retiros:</Text>
                <Input
                  type="number"
                  placeholder="Ej: 1"
                  value={state.filters.minWithdrawals}
                  onChange={(e) => handleFilterChange('minWithdrawals', e.target.value ? parseInt(e.target.value) : undefined)}
                  min={0}
                />
              </Col>
              <Col span={12}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Máximo retiros:</Text>
                <Input
                  type="number"
                  placeholder="Ej: 50"
                  value={state.filters.maxWithdrawals}
                  onChange={(e) => handleFilterChange('maxWithdrawals', e.target.value ? parseInt(e.target.value) : undefined)}
                  min={0}
                />
              </Col>
            </Row>
          </FilterSection>

          {/* Patrones Horarios */}
          <FilterSection
            icon="🌙"
            title="Patrones Horarios"
            description="Optimiza timing según hábitos del usuario"
            explanationKey="hourlyBehavior"
            hasActiveFilters={!!(state.filters.depositBehavior || state.filters.withdrawalBehavior)}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Patrón depósitos:</Text>
                <Select
                  placeholder="Seleccionar patrón"
                  value={state.filters.depositBehavior}
                  onChange={(value) => handleFilterChange('depositBehavior', value)}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="diurno">☀️ Diurno (6:00-20:00)</Option>
                  <Option value="nocturno">🌙 Nocturno (20:00-6:00)</Option>
                  <Option value="mixto">🌗 Mixto</Option>
                </Select>
              </Col>
              <Col span={12}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Patrón retiros:</Text>
                <Select
                  placeholder="Seleccionar patrón"
                  value={state.filters.withdrawalBehavior}
                  onChange={(value) => handleFilterChange('withdrawalBehavior', value)}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="diurno">☀️ Diurno (6:00-20:00)</Option>
                  <Option value="nocturno">🌙 Nocturno (20:00-6:00)</Option>
                  <Option value="mixto">🌗 Mixto</Option>
                </Select>
              </Col>
            </Row>
          </FilterSection>

          {/* Segmentación VIP */}
          <FilterSection
            icon="💎"
            title="Segmentación VIP"
            description="Usuarios de alto valor para atención personalizada"
            explanationKey="highValue"
            hasActiveFilters={!!state.filters.highValueThreshold}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Monto mínimo total depositado:</Text>
                <Input
                  type="number"
                  placeholder="Ej: 50000"
                  value={state.filters.highValueThreshold}
                  onChange={(e) => handleFilterChange('highValueThreshold', e.target.value ? parseFloat(e.target.value) : undefined)}
                  min={0}
                  prefix="$"
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>
          </FilterSection>

          {/* Detección de Inactividad */}
          <FilterSection
            icon="⏰"
            title="Detección de Inactividad"
            description="Identifica usuarios en riesgo de abandono"
            explanationKey="inactivity"
            hasActiveFilters={!!state.filters.inactivityPeriod}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Tiempo inactividad:</Text>
                <Input
                  type="number"
                  placeholder="Cantidad"
                  value={state.filters.inactivityPeriod?.value}
                  onChange={(e) => {
                    const value = e.target.value ? parseInt(e.target.value) : undefined;
                    handleFilterChange('inactivityPeriod', value ? {
                      value,
                      unit: state.filters.inactivityPeriod?.unit || 'days'
                    } : undefined);
                  }}
                  min={1}
                />
              </Col>
              <Col span={12}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Unidad:</Text>
                <Select
                  placeholder="Unidad"
                  value={state.filters.inactivityPeriod?.unit}
                  onChange={(unit) => {
                    const value = state.filters.inactivityPeriod?.value;
                    handleFilterChange('inactivityPeriod', value ? { value, unit } : undefined);
                  }}
                  style={{ width: '100%' }}
                >
                  <Option value="minutes">Minutos</Option>
                  <Option value="hours">Horas</Option>
                  <Option value="days">Días</Option>
                  <Option value="weeks">Semanas</Option>
                  <Option value="months">Meses</Option>
                </Select>
              </Col>
              {state.filters.inactivityPeriod && (
                <Col span={24}>
                  <Alert
                    message={`Usuarios inactivos por más de ${state.filters.inactivityPeriod.value} ${
                      state.filters.inactivityPeriod.unit === 'minutes' ? 'minutos' :
                      state.filters.inactivityPeriod.unit === 'hours' ? 'horas' :
                      state.filters.inactivityPeriod.unit === 'days' ? 'días' :
                      state.filters.inactivityPeriod.unit === 'weeks' ? 'semanas' : 'meses'
                    }`}
                    type="info"
                    showIcon
                    style={{ fontSize: '11px' }}
                  />
                </Col>
              )}
            </Row>
          </FilterSection>

          {/* Día Más Activo */}
          <FilterSection
            icon="📅"
            title="Día Más Activo"
            description="Optimización temporal basada en patrones semanales"
            explanationKey="mostActiveDay"
            hasActiveFilters={!!state.filters.mostActiveDay}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Día de la semana:</Text>
                <Select
                  placeholder="Seleccionar día"
                  value={state.filters.mostActiveDay}
                  onChange={(value) => handleFilterChange('mostActiveDay', value)}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="monday">🌙 Lunes</Option>
                  <Option value="tuesday">🔥 Martes</Option>
                  <Option value="wednesday">🌟 Miércoles</Option>
                  <Option value="thursday">⚡ Jueves</Option>
                  <Option value="friday">🎉 Viernes</Option>
                  <Option value="saturday">🌈 Sábado</Option>
                  <Option value="sunday">☀️ Domingo</Option>
                </Select>
              </Col>
            </Row>
          </FilterSection>

          {/* Regreso Post-Inactividad */}
          <FilterSection
            icon="🔄"
            title="Regreso Post-Inactividad"
            description="Detecta usuarios que regresaron tras período de ausencia"
            explanationKey="returnAfterInactivity"
            hasActiveFilters={!!state.filters.returnAfterInactivity}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Tipo de actividad:</Text>
                <Select
                  placeholder="Seleccionar acción"
                  value={state.filters.returnAfterInactivity?.action}
                  onChange={(action) => {
                    if (action) {
                      const newFilter = {
                        action,
                        recentPeriod: state.filters.returnAfterInactivity?.recentPeriod || { start: '', end: '' },
                        inactivePeriod: state.filters.returnAfterInactivity?.inactivePeriod || { start: '', end: '' }
                      };
                      handleFilterChange('returnAfterInactivity', newFilter);
                    } else {
                      handleFilterChange('returnAfterInactivity', undefined);
                    }
                  }}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="DEPOSIT">💰 Depósitos</Option>
                  <Option value="WITHDRAW">💸 Retiros</Option>
                </Select>
              </Col>

              {state.filters.returnAfterInactivity?.action && (
                <>
                  <Col span={24}>
                    <Divider orientation="left" style={{ margin: '16px 0 8px 0' }}>
                      <Text strong style={{ color: '#52c41a' }}>Período Reciente (SÍ hubo actividad)</Text>
                    </Divider>
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ marginBottom: 8, display: 'block' }}>Fecha inicio:</Text>
                    <DatePicker
                      value={state.filters.returnAfterInactivity?.recentPeriod.start ? 
                        dayjs(state.filters.returnAfterInactivity.recentPeriod.start) : null}
                      onChange={(date) => {
                        const currentFilter = state.filters.returnAfterInactivity;
                        if (currentFilter) {
                          const newFilter = {
                            ...currentFilter,
                            recentPeriod: {
                              ...currentFilter.recentPeriod,
                              start: date ? date.format('YYYY-MM-DD') : ''
                            }
                          };
                          handleFilterChange('returnAfterInactivity', newFilter);
                        }
                      }}
                      format="DD/MM/YYYY"
                      style={{ width: '100%' }}
                      placeholder="Inicio"
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ marginBottom: 8, display: 'block' }}>Fecha fin:</Text>
                    <DatePicker
                      value={state.filters.returnAfterInactivity?.recentPeriod.end ? 
                        dayjs(state.filters.returnAfterInactivity.recentPeriod.end) : null}
                      onChange={(date) => {
                        const currentFilter = state.filters.returnAfterInactivity;
                        if (currentFilter) {
                          const newFilter = {
                            ...currentFilter,
                            recentPeriod: {
                              ...currentFilter.recentPeriod,
                              end: date ? date.format('YYYY-MM-DD') : ''
                            }
                          };
                          handleFilterChange('returnAfterInactivity', newFilter);
                        }
                      }}
                      format="DD/MM/YYYY"
                      style={{ width: '100%' }}
                      placeholder="Fin"
                    />
                  </Col>

                  <Col span={24}>
                    <Divider orientation="left" style={{ margin: '16px 0 8px 0' }}>
                      <Text strong style={{ color: '#ff4d4f' }}>Período Anterior (NO hubo actividad)</Text>
                    </Divider>
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ marginBottom: 8, display: 'block' }}>Fecha inicio:</Text>
                    <DatePicker
                      value={state.filters.returnAfterInactivity?.inactivePeriod.start ? 
                        dayjs(state.filters.returnAfterInactivity.inactivePeriod.start) : null}
                      onChange={(date) => {
                        const currentFilter = state.filters.returnAfterInactivity;
                        if (currentFilter) {
                          const newFilter = {
                            ...currentFilter,
                            inactivePeriod: {
                              ...currentFilter.inactivePeriod,
                              start: date ? date.format('YYYY-MM-DD') : ''
                            }
                          };
                          handleFilterChange('returnAfterInactivity', newFilter);
                        }
                      }}
                      format="DD/MM/YYYY"
                      style={{ width: '100%' }}
                      placeholder="Inicio"
                    />
                  </Col>
                  <Col span={12}>
                    <Text strong style={{ marginBottom: 8, display: 'block' }}>Fecha fin:</Text>
                    <DatePicker
                      value={state.filters.returnAfterInactivity?.inactivePeriod.end ? 
                        dayjs(state.filters.returnAfterInactivity.inactivePeriod.end) : null}
                      onChange={(date) => {
                        const currentFilter = state.filters.returnAfterInactivity;
                        if (currentFilter) {
                          const newFilter = {
                            ...currentFilter,
                            inactivePeriod: {
                              ...currentFilter.inactivePeriod,
                              end: date ? date.format('YYYY-MM-DD') : ''
                            }
                          };
                          handleFilterChange('returnAfterInactivity', newFilter);
                        }
                      }}
                      format="DD/MM/YYYY"
                      style={{ width: '100%' }}
                      placeholder="Fin"
                    />
                  </Col>

                  {state.filters.returnAfterInactivity?.recentPeriod.start && 
                   state.filters.returnAfterInactivity?.recentPeriod.end &&
                   state.filters.returnAfterInactivity?.inactivePeriod.start &&
                   state.filters.returnAfterInactivity?.inactivePeriod.end && (
                    <Col span={24}>
                      <Alert
                        message={
                          <div>
                            <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                              🎯 Filtro configurado correctamente
                            </div>
                            <div style={{ fontSize: '12px' }}>
                              Detectando usuarios que {state.filters.returnAfterInactivity.action === 'DEPOSIT' ? 'depositaron' : 'retiraron'} entre{' '}
                              {dayjs(state.filters.returnAfterInactivity.recentPeriod.start).format('DD/MM')} y{' '}
                              {dayjs(state.filters.returnAfterInactivity.recentPeriod.end).format('DD/MM')} después de estar inactivos.
                            </div>
                          </div>
                        }
                        type="success"
                        showIcon
                        style={{ marginTop: 12 }}
                      />
                    </Col>
                  )}
                </>
              )}
            </Row>
          </FilterSection>

          {/* Ciclo Único Completo */}
          <FilterSection
            icon="🔄"
            title="Ciclo Único Completo"
            description="Usuarios que completaron exactamente un ciclo Depósito→Retiro"
            explanationKey="singleCycle"
            hasActiveFilters={!!state.filters.singleCompleteCycleOnly}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Checkbox
                  checked={state.filters.singleCompleteCycleOnly === true}
                  onChange={(e) => handleFilterChange('singleCompleteCycleOnly', e.target.checked ? true : undefined)}
                >
                  <div>
                    <Text strong>Solo usuarios con un ciclo completo</Text>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      Conocen el proceso pero no se engancharon - candidatos ideales para remarketing
                    </div>
                  </div>
                </Checkbox>
              </Col>
            </Row>
          </FilterSection>

          {/* Análisis de Desviación de Montos */}
          <FilterSection
            icon="📊"
            title="Desviación de Montos"
            description="Detecta comportamientos atípicos comparando con historial"
            explanationKey="amountAnalysis"
            hasActiveFilters={!!state.filters.lastAmountVsAverage}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Tipo operación:</Text>
                <Select
                  placeholder="Seleccionar tipo"
                  value={state.filters.lastAmountVsAverage?.operationType}
                  onChange={(operationType) => {
                    if (operationType) {
                      const newFilter = {
                        operationType,
                        comparisonType: state.filters.lastAmountVsAverage?.comparisonType || 'GREATER',
                        significanceThreshold: state.filters.lastAmountVsAverage?.significanceThreshold || 50,
                        minimumOperations: state.filters.lastAmountVsAverage?.minimumOperations || 3
                      };
                      handleFilterChange('lastAmountVsAverage', newFilter);
                    } else {
                      handleFilterChange('lastAmountVsAverage', undefined);
                    }
                  }}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="DEPOSIT">💰 Depósitos</Option>
                  <Option value="WITHDRAW">💸 Retiros</Option>
                </Select>
              </Col>
              <Col span={12}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Comparación:</Text>
                <Select
                  placeholder="Mayor/menor"
                  value={state.filters.lastAmountVsAverage?.comparisonType}
                  onChange={(comparisonType) => {
                    const currentFilter = state.filters.lastAmountVsAverage;
                    if (currentFilter && comparisonType) {
                      const newFilter = { ...currentFilter, comparisonType };
                      handleFilterChange('lastAmountVsAverage', newFilter);
                    }
                  }}
                  style={{ width: '100%' }}
                  disabled={!state.filters.lastAmountVsAverage?.operationType}
                >
                  <Option value="GREATER">📈 Significativamente MAYOR</Option>
                  <Option value="LESS">📉 Significativamente MENOR</Option>
                </Select>
              </Col>
              <Col span={12}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Umbral diferencia (%):</Text>
                <Input
                  type="number"
                  placeholder="Ej: 50"
                  value={state.filters.lastAmountVsAverage?.significanceThreshold}
                  onChange={(e) => {
                    const currentFilter = state.filters.lastAmountVsAverage;
                    if (currentFilter) {
                      const threshold = e.target.value ? parseFloat(e.target.value) : 50;
                      const newFilter = { ...currentFilter, significanceThreshold: threshold };
                      handleFilterChange('lastAmountVsAverage', newFilter);
                    }
                  }}
                  min={1}
                  max={500}
                  suffix="%"
                  style={{ width: '100%' }}
                  disabled={!state.filters.lastAmountVsAverage?.operationType}
                />
              </Col>
              <Col span={12}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Mín. ops. históricas:</Text>
                <Input
                  type="number"
                  placeholder="Ej: 3"
                  value={state.filters.lastAmountVsAverage?.minimumOperations}
                  onChange={(e) => {
                    const currentFilter = state.filters.lastAmountVsAverage;
                    if (currentFilter) {
                      const minOps = e.target.value ? parseInt(e.target.value) : 3;
                      const newFilter = { ...currentFilter, minimumOperations: minOps };
                      handleFilterChange('lastAmountVsAverage', newFilter);
                    }
                  }}
                  min={2}
                  max={20}
                  style={{ width: '100%' }}
                  disabled={!state.filters.lastAmountVsAverage?.operationType}
                />
              </Col>
            </Row>
          </FilterSection>

          {/* VIP Inactivos */}
          <FilterSection
            icon="💰"
            title="VIP Inactivos"
            description="Usuarios de alto valor en riesgo de pérdida"
            explanationKey="highValueInactive"
            hasActiveFilters={!!state.filters.highValueButInactiveFilter}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Valor mínimo total:</Text>
                <Input
                  type="number"
                  placeholder="Ej: 50000"
                  value={state.filters.highValueButInactiveFilter?.minTotalDeposits}
                  onChange={(e) => {
                    const minTotalDeposits = e.target.value ? parseFloat(e.target.value) : undefined;
                    if (minTotalDeposits !== undefined) {
                      const newFilter = {
                        minTotalDeposits,
                        daysSinceLastDeposit: state.filters.highValueButInactiveFilter?.daysSinceLastDeposit || 30
                      };
                      handleFilterChange('highValueButInactiveFilter', newFilter);
                    } else {
                      handleFilterChange('highValueButInactiveFilter', undefined);
                    }
                  }}
                  min={0}
                  prefix="$"
                  style={{ width: '100%' }}
                />
              </Col>
              <Col span={12}>
                <Text strong style={{ marginBottom: 8, display: 'block' }}>Días sin depositar:</Text>
                <Input
                  type="number"
                  placeholder="Ej: 30"
                  value={state.filters.highValueButInactiveFilter?.daysSinceLastDeposit}
                  onChange={(e) => {
                    const daysSinceLastDeposit = e.target.value ? parseInt(e.target.value) : undefined;
                    if (daysSinceLastDeposit !== undefined && state.filters.highValueButInactiveFilter?.minTotalDeposits) {
                      const newFilter = {
                        minTotalDeposits: state.filters.highValueButInactiveFilter.minTotalDeposits,
                        daysSinceLastDeposit
                      };
                      handleFilterChange('highValueButInactiveFilter', newFilter);
                    }
                  }}
                  min={1}
                  suffix="días"
                  style={{ width: '100%' }}
                  disabled={!state.filters.highValueButInactiveFilter?.minTotalDeposits}
                />
              </Col>
              {state.filters.highValueButInactiveFilter?.minTotalDeposits && 
               state.filters.highValueButInactiveFilter?.daysSinceLastDeposit && (
                <Col span={24}>
                  <Alert
                    message={
                      <div>
                        <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                          🚨 Alerta VIP configurada
                        </div>
                        <div style={{ fontSize: '12px' }}>
                          Usuarios con ${state.filters.highValueButInactiveFilter.minTotalDeposits.toLocaleString()}+ sin depositar hace {state.filters.highValueButInactiveFilter.daysSinceLastDeposit}+ días
                        </div>
                      </div>
                    }
                    type="warning"
                    showIcon
                    style={{ marginTop: 12 }}
                  />
                </Col>
              )}
            </Row>
          </FilterSection>
        </div>

        {/* Action Buttons */}
        <div className="filter-actions">
          <Space style={{ width: '100%', justifyContent: 'center' }} size="large">
            <Button
              type="primary"
              onClick={() => {
                applyFilters();
                setFilterDrawerVisible(false);
              }}
              size="large"
              icon={<FilterOutlined />}
              style={{
                minWidth: '160px',
                height: '44px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                border: 'none',
                boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
              }}
            >
              Aplicar Filtros
            </Button>
            <Button
              onClick={clearFilters}
              size="large"
              icon={<ClearOutlined />}
              style={{
                minWidth: '160px',
                height: '44px',
                borderRadius: '8px',
                border: '1px solid #d9d9d9'
              }}
            >
              Limpiar Filtros
            </Button>
          </Space>
        </div>
      </Drawer>

      {/* Contador de selección */}
      {state.selectedUsers.length > 0 && (
        <div style={{ 
          marginBottom: 16, 
          padding: '12px 16px', 
          background: '#f0f9ff', 
          border: '1px solid #91d5ff', 
          borderRadius: '6px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <Text strong style={{ color: '#1890ff' }}>
              🎯 {state.selectedUsers.length} usuario{state.selectedUsers.length !== 1 ? 's' : ''} seleccionado{state.selectedUsers.length !== 1 ? 's' : ''} para la campaña
            </Text>
          </div>
          <Button 
            size="small" 
            onClick={clearUserSelection}
            style={{ color: '#1890ff' }}
            type="link"
          >
            Limpiar selección
          </Button>
        </div>
      )}

      {/* Tabla de usuarios - PAGINACIÓN REAL */}
      <Table<UserTableMetrics>
        columns={columns}
        dataSource={(state.data?.users || []).filter(user => user.userId)}
        loading={isLoading}
        rowKey="userId"
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: state.selectedUsers,
          onChange: handleUserSelection,
          getCheckboxProps: (record: any) => ({
            disabled: !record.userId,
            name: record.userId,
          }),
          columnWidth: 50,
          fixed: true,
          selections: [
            {
              key: 'all',
              text: 'Seleccionar todos en esta página',
              onSelect: () => {
                const currentPageUserIds = (state.data?.users || [])
                  .filter(user => user.userId)
                  .map(user => user.userId);
                const newSelection = [...new Set([...state.selectedUsers, ...currentPageUserIds])];
                setState(prev => ({ ...prev, selectedUsers: newSelection }));
              },
            },
            {
              key: 'none',
              text: 'Deseleccionar todos',
              onSelect: () => {
                setState(prev => ({ ...prev, selectedUsers: [] }));
              },
            },
          ],
        }}
        rowClassName={(record) => {
          return state.selectedUsers.includes(record.userId) ? 'selected-row' : '';
        }}
        pagination={{
          current: state.currentPage,
          pageSize: state.pageSize,
          total: state.data?.totalUsersFiltered || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} usuarios filtrados`,
          pageSizeOptions: ['15', '30', '50', '100'],
          onChange: handlePageChange,
          onShowSizeChange: handlePageSizeChange,
        }}
        scroll={{ x: 1460 }}
        size="small"
      />
    </Card>

    {/* Modal para crear/editar/ver campaña */}
    <Modal
      title={
        state.campaignModal.mode === 'create' ? 'Crear Nueva Campaña' :
        state.campaignModal.mode === 'edit' ? 'Editar Campaña' :
        'Detalles de Campaña'
      }
      open={state.campaignModal.visible}
      onCancel={handleCloseCampaignModal}
      footer={state.campaignModal.mode === 'view' ? [
        <Button key="close" onClick={handleCloseCampaignModal}>
          Cerrar
        </Button>
      ] : null}
      width={700}
      destroyOnClose
    >
      <CampaignFormModal
        campaign={state.campaignModal.campaign}
        mode={state.campaignModal.mode}
        userCount={state.data?.totalUsersFiltered || 0}
        onSave={handleSaveCampaign}
        onCancel={handleCloseCampaignModal}
      />
    </Modal>
  </div>
);
  };

  // Componente del formulario de campaña
  interface CampaignFormModalProps {
    campaign?: Campaign;
    mode: 'create' | 'edit' | 'view';
    userCount: number;
    onSave: (data: { name: string; objective: 'REMARKETING' | 'POSTBLOQUEO'; message: string }) => void;
    onCancel: () => void;
  }

  const CampaignFormModal: React.FC<CampaignFormModalProps> = ({
    campaign,
    mode,
    userCount,
    onSave,
    onCancel,
  }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // Plantillas de mensaje
    const messageTemplates = {
      REMARKETING: [
        '¡Hola! Hemos notado que no has estado activo últimamente. ¿Te gustaría volver a probar suerte? 🎰',
        'Te echamos de menos. ¡Regresa y disfruta de nuestros juegos con bonificaciones especiales! 🎁',
        '¿Listo para otra ronda? Vuelve y descubre las nuevas opciones que tenemos para ti. 🎲',
      ],
      POSTBLOQUEO: [
        'Entendemos que puedas necesitar un descanso. Si decides volver, estaremos aquí para ti. 🤝',
        'Tu bienestar es importante para nosotros. Si necesitas ayuda o quieres volver, contáctanos. 💬',
        'Respetamos tu decisión. Si cambias de opinión, siempre serás bienvenido de vuelta. ✨',
      ],
    };

    // Inicializar formulario
    useEffect(() => {
      if (campaign) {
        form.setFieldsValue({
          name: campaign.name,
          objective: campaign.objective,
          message: campaign.message,
        });
      } else {
        form.setFieldsValue({
          name: `Campaña ${new Date().toLocaleDateString()}`,
          objective: 'REMARKETING',
          message: messageTemplates.REMARKETING[0],
        });
      }
    }, [campaign, form]);

    // Manejar envío
    const handleSubmit = async (values: any) => {
      setLoading(true);
      try {
        await onSave(values);
      } finally {
        setLoading(false);
      }
    };

    // Aplicar plantilla
    const applyTemplate = (template: string) => {
      form.setFieldValue('message', template);
    };

    const objective = Form.useWatch('objective', form);

    if (mode === 'view') {
      return (
        <div style={{ padding: '16px 0' }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong>Nombre:</Text>
                    <div>{campaign?.name}</div>
                  </Col>
                  <Col span={12}>
                    <Text strong>Objetivo:</Text>
                    <div>
                      <Tag color={campaign?.objective === 'REMARKETING' ? 'blue' : 'orange'}>
                        {campaign?.objective === 'REMARKETING' ? 'Remarketing' : 'Post-bloqueo'}
                      </Tag>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col span={24}>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Text strong>Estado:</Text>
                <div style={{ marginTop: 8 }}>
                  <Tag color={
                    campaign?.status === 'DRAFT' ? 'warning' :
                    campaign?.status === 'IN_PROGRESS' ? 'processing' :
                    campaign?.status === 'COMPLETED' ? 'success' : 'error'
                  }>
                    {campaign?.status === 'DRAFT' ? 'Listo para Iniciar' :
                     campaign?.status === 'IN_PROGRESS' ? 'En Progreso' :
                     campaign?.status === 'COMPLETED' ? 'Completada' : 'Cancelada'}
                  </Tag>
                </div>
              </Card>
            </Col>
            <Col span={24}>
              <Card size="small" style={{ marginBottom: 16 }}>
                <Text strong>Usuarios Objetivo:</Text>
                <div style={{ marginTop: 8 }}>
                  <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                    {campaign?.selectedUsers || 0}
                  </Text>
                  <Text type="secondary"> usuarios seleccionados</Text>
                </div>
              </Card>
            </Col>
            <Col span={24}>
              <Card size="small">
                <Text strong>Mensaje:</Text>
                <div style={{ 
                  marginTop: 8, 
                  padding: '12px', 
                  background: '#f5f5f5', 
                  borderRadius: '6px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {campaign?.message}
                </div>
              </Card>
            </Col>
            <Col span={24}>
              <Card size="small">
                <Text strong>Fechas:</Text>
                <div style={{ marginTop: 8 }}>
                  <div><Text type="secondary">Creada:</Text> {campaign?.createdAt ? dayjs(campaign.createdAt).format('DD/MM/YYYY HH:mm') : 'N/A'}</div>
                  {campaign?.startedAt && (
                    <div><Text type="secondary">Iniciada:</Text> {dayjs(campaign.startedAt).format('DD/MM/YYYY HH:mm')}</div>
                  )}
                  {campaign?.completedAt && (
                    <div><Text type="secondary">Completada:</Text> {dayjs(campaign.completedAt).format('DD/MM/YYYY HH:mm')}</div>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      );
    }

    return (
      <div style={{ padding: '16px 0' }}>
        {mode === 'create' && (
          <Card size="small" style={{ marginBottom: 16, background: '#f0f9ff', border: '1px solid #91d5ff' }}>
            <Row align="middle" gutter={16}>
              <Col>
                <div style={{ 
                  width: '32px', 
                  height: '32px', 
                  borderRadius: '50%', 
                  background: '#1890ff', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <span style={{ color: 'white', fontSize: '16px' }}>🎯</span>
                </div>
              </Col>
              <Col flex={1}>
                <Space direction="vertical" size={2}>
                  <Text strong style={{ color: '#1890ff' }}>
                    Campaña con Filtros Aplicados
                  </Text>
                  <Text style={{ fontSize: '12px', color: '#666' }}>
                    Esta campaña se enviará a <strong>{userCount} usuarios</strong> basándose en los filtros aplicados en la tabla.
                  </Text>
                </Space>
              </Col>
            </Row>
          </Card>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Nombre de la Campaña"
                rules={[
                  { required: true, message: 'El nombre es requerido' },
                  { min: 3, message: 'Mínimo 3 caracteres' },
                  { max: 100, message: 'Máximo 100 caracteres' },
                ]}
              >
                <Input placeholder="Ej: Campaña Remarketing Enero 2024" showCount maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="objective"
                label="Objetivo de la Campaña"
                rules={[{ required: true, message: 'El objetivo es requerido' }]}
              >
                <Select placeholder="Seleccionar objetivo">
                  <Option value="REMARKETING">
                    <div>
                      <div><strong>Remarketing</strong></div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Reactivar usuarios inactivos
                      </div>
                    </div>
                  </Option>
                  <Option value="POSTBLOQUEO">
                    <div>
                      <div><strong>Post-bloqueo</strong></div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Seguimiento tras autoexclusión
                      </div>
                    </div>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="message"
            label="Mensaje a Enviar"
            rules={[
              { required: true, message: 'El mensaje es requerido' },
              { max: 1000, message: 'Máximo 1000 caracteres' },
            ]}
          >
            <TextArea
              placeholder="Escribe el mensaje que se enviará a los usuarios..."
              rows={4}
              showCount
              maxLength={1000}
            />
          </Form.Item>

          {objective && messageTemplates[objective as keyof typeof messageTemplates] && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
                Plantillas Sugeridas:
              </Title>
              <Space direction="vertical" style={{ width: '100%' }}>
                {messageTemplates[objective as keyof typeof messageTemplates].map((template, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '8px 12px',
                      background: '#f5f5f5',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      border: '1px solid transparent',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#e6f7ff';
                      e.currentTarget.style.borderColor = '#1890ff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f5f5f5';
                      e.currentTarget.style.borderColor = 'transparent';
                    }}
                    onClick={() => applyTemplate(template)}
                  >
                    <Text style={{ fontSize: '13px' }}>{template}</Text>
                  </div>
                ))}
              </Space>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: 8 }}>
                Haz clic en cualquier plantilla para aplicarla
              </Text>
            </Card>
          )}

          <Row justify="end">
            <Space>
              <Button onClick={onCancel}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {mode === 'create' ? 'Crear Campaña' : 'Actualizar Campaña'}
              </Button>
            </Space>
          </Row>
        </Form>
      </div>
    );
  };